import React from "react";
import { Link } from "react-router-dom";
import { Plane, Check, Phone, ArrowRight } from "lucide-react";
import { SITE } from "../data/site";
import BookingForm from "../components/BookingForm";

const AIRPORTS = [
  { code: "IAD", name: "Dulles International Airport", desc: "Washington Dulles · 26mi from DC", features: ["Flight tracking", "Meet & Greet", "Private jet terminals"] },
  { code: "DCA", name: "Reagan National Airport", desc: "Arlington VA · 5mi from DC", features: ["Curbside express pickup", "Flight tracking", "Live dispatcher coordination"] },
  { code: "BWI", name: "Baltimore/Washington International", desc: "Maryland · 30mi from DC", features: ["Flight tracking", "Meet & Greet", "Long-distance rates"] },
];

export default function AirportTransportation() {
  return (
    <div className="pt-20 bg-[#050505] min-h-screen" data-testid="airport-page">
      <section className="relative h-[55vh] min-h-[440px] flex items-end overflow-hidden">
        <img src={SITE.heroMixed} alt="Airport transportation Washington DC" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/60 to-black/30" />
        <div className="relative max-w-7xl mx-auto px-6 pb-14 w-full">
          <div className="label-eyebrow mb-3">Airport Transportation</div>
          <h1 className="font-display text-5xl sm:text-6xl text-white tracking-tight max-w-4xl">
            Luxury airport transfers · IAD · DCA · BWI
          </h1>
          <p className="text-white/75 text-lg mt-4 max-w-2xl">
            Executive sedans, SUVs and sprinter vans with real-time flight tracking,
            meet & greet, and private jet terminal access throughout the DMV.
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-6">
          {AIRPORTS.map((a) => (
            <div key={a.code} className="lux-card p-8" data-testid={`airport-${a.code}`}>
              <Plane size={22} className="text-[#d4af6a] mb-4" />
              <div className="label-eyebrow">{a.code}</div>
              <h3 className="font-display text-2xl text-white mt-1">{a.name}</h3>
              <p className="text-white/55 text-sm mt-1">{a.desc}</p>
              <ul className="mt-6 space-y-2.5">
                {a.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-white/75">
                    <Check size={14} className="text-[#d4af6a] mt-1 flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <h2 className="font-display text-4xl text-white tracking-tight mb-5">
              On-time, every time. Across every DMV airport.
            </h2>
            <p className="text-white/70 leading-relaxed">
              USA Limo Service has provided <strong className="text-white">premium airport transportation in Washington DC </strong>
              since 1984. Whether you're landing at Dulles, Reagan, or BWI, our dispatch team tracks your
              flight in real time, and your chauffeur and luxury vehicle will be waiting curbside, at baggage
              claim, or at the private jet terminal — whichever you prefer.
            </p>
            <ul className="mt-7 space-y-3">
              {[
                "Real-time flight tracking & delay adjustment",
                "Executive Meet & Greet (no waiting)",
                "Private jet terminal pickups (FBO)",
                "Cadillac Escalade · Suburban · Yukon · Sprinter Van",
                "Corporate accounts with priority dispatch",
              ].map((p) => (
                <li key={p} className="flex items-start gap-3 text-white/80">
                  <Check size={18} className="text-[#d4af6a] mt-0.5" /> {p}
                </li>
              ))}
            </ul>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/book" className="btn-accent">Reserve Airport Transfer <ArrowRight size={14} /></Link>
              <a href={`tel:${SITE.phonesTel.dispatch}`} className="btn-outline">
                <Phone size={14} /> {SITE.phones.dispatch}
              </a>
            </div>
          </div>
          <div className="lg:col-span-6">
            <BookingForm eyebrow="Reserve Airport Pickup" defaultAirport />
          </div>
        </div>
      </section>
    </div>
  );
}
